import { DatePipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import _ from 'lodash';
import { map } from 'rxjs/operators';

import { Feature } from '../entities/feature.entity';
import { WebApiService } from './web-api.service';

@Injectable({
  providedIn: 'root',
})
export class FeatureService {
  static readonly FEATURE_PERIMETER_WD = 'perimeter-sgps-wd';
  static readonly FEATURE_PERIMETER_WS_MEAN = 'perimeter-sgps-ws-mean';
  static readonly FEATURE_PERIMETER_WA = 'perimeter-wa'; // NOT FOUND
  static readonly FEATURE_PERIMETER_TWT = 'perimeter-twt';
  static readonly FEATURE_PERIMETER_COMP = 'perimeter-comp'; // NOT FOUND
  static readonly FEATURE_6MWT_6MIN_WD = '6mwt-6min_sgps-wd';
  static readonly FEATURE_6MWT_6MIN_WS_MEAN = '6mwt-6min_sgps-ws-mean';
  static readonly FEATURE_6MWT_WA = '6mwt-wa'; // NOT FOUND
  static readonly FEATURE_6MWT_TWT = '6mwt-twt';
  static readonly FEATURE_DRAW_LEFT_DS_MEAN = 'draw-left-ds-mean'; // NOT FOUND
  static readonly FEATURE_DRAW_RIGHT_DS_MEAN = 'draw-right-ds-mean'; // NOT FOUND
  static readonly FEATURE_DRAW_LEFT_PWD_MEAN = 'draw-left-pwd-mean';
  static readonly FEATURE_DRAW_RIGHT_PWD_MEAN = 'draw-right-pwd-mean';
  static readonly FEATURE_DRAW_LEFT_CT_MEAN = 'draw-left-ct-mean';
  static readonly FEATURE_DRAW_RIGHT_CT_MEAN = 'draw-right-ct-mean';
  static readonly FEATURE_CPS_STD_AR_RT_MEAN = 'cps-std_ar-rt-mean';
  static readonly FEATURE_CPS_STD_ACC = 'cps-std-acc'; // NOT FOUND
  static readonly FEATURE_CPS_STD_CRC = 'cps-std-crc';
  static readonly FEATURE_VISION_BIN_MONOYER = 'vision-bin-monoyer';
  static readonly FEATURE_VISION_MONOL_MONOYER = 'vision-monol-monoyer';
  static readonly FEATURE_VISION_MONOR_MONOYER = 'vision-monor-monoyer';
  static readonly FEATURE_BLCS_SCR = 'blcs-scr';
  static readonly FEATURE_BWCS_SCR = 'bwcs-scr';
  static readonly FEATURE_MFIS5_SCR = 'mfis5-scr';
  static readonly FEATURE_PHQ8_SCR = 'phq8-scr';
  static readonly FEATURE_SF36_1_SCR = 'sf36-PF-scr';
  static readonly FEATURE_SF36_2_SCR = 'sf36-VT-scr';
  static readonly FEATURE_SF36_3_SCR = 'sf36-RP-scr';
  static readonly FEATURE_SF36_4_SCR = 'sf36-MH-scr';
  static readonly FEATURE_SF36_5_SCR = 'sf36-RE-scr';
  static readonly FEATURE_SF36_6_SCR = 'sf36-SF-scr';
  static readonly FEATURE_SF36_7_SCR = 'sf36-BP-scr';
  static readonly FEATURE_SF36_8_SCR = 'sf36-GH-scr';
  static readonly FEATURE_SF36_MENTAL_HEALTH_SCR = 'sf36-mental_health-scr';
  static readonly FEATURE_SF36_PHYSICAL_HEALTH_SCR = 'sf36-physical_health-scr';
  static readonly FEATURE_PS_SCR = 'pain-scr';

  private readonly GET_REQUEST_PATH = '/api/v1.1/feature';

  constructor(
    private webApiService: WebApiService,
    private datePipe: DatePipe
  ) {}

  /**
   * Groups features by second
   * @param origin
   * @param item
   * @returns Object
   */
  private groupBySecond(origin: any, item: Feature): any {
    const key = this.datePipe.transform(item.startDate, 'yyyy-MM-dd HH:mm:ss');

    if (origin[key] === undefined) {
      origin[key] = [];
    }

    origin[key].push(item);

    return origin;
  }

  /**
   * Group features by feature id
   * @param features
   * @param featureId
   * @returns Feature lists
   */
  public groupFeatures(features: Feature[], featureId: string): Feature[][] {
    let data: any = {};

    // Gets features by code
    let filteredFeatures = features.filter((item) => item.getId() == featureId);
    filteredFeatures.sort((a, b) => {
      return (a.startDate as Date).getTime() - (b.startDate as Date).getTime();
    });

    data = filteredFeatures.reduce(this.groupBySecond.bind(this), {});

    return data;
  }

  /**
   * Gets features list from database
   * @param params
   * @returns Observable
   */
  public getFeatures(params: any = {}): Observable<Feature[]> {
    let queryParams = new HttpParams();

    Object.keys(params).forEach((key) => {
      if (Array.isArray(params[key])) {
        params[key].forEach((item) => {
          queryParams = queryParams.append(key, item);
        });
      } else {
        queryParams = queryParams.append(key, params[key]);
      }
    });

    return this.webApiService
      .get(this.GET_REQUEST_PATH, true, { params: queryParams })
      .pipe(
        map((response) => {
          return response.data.map((item) =>
            this.transformValue(new Feature().deserialize(item))
          );
        })
      );
  }

  /**
   * Transforms value of specific feature
   * @param feature
   * @returns feature
   */
  private transformValue(feature: Feature): Feature {
    let clonedFeature = _.cloneDeep(feature);
    switch (feature.getId()) {
      case FeatureService.FEATURE_6MWT_6MIN_WS_MEAN:
        clonedFeature.featureValue = (clonedFeature.featureValue * 3600) / 1000;
        break;
      case FeatureService.FEATURE_PERIMETER_TWT:
      case FeatureService.FEATURE_6MWT_TWT:
        let mins = Math.floor(clonedFeature.featureValue / 60);
        let seconds = (clonedFeature.featureValue - mins * 60) / 100;

        clonedFeature.featureValue = mins + seconds;
        break;
      case FeatureService.FEATURE_CPS_STD_ACC:
        clonedFeature.featureValue = clonedFeature.featureValue * 100;
        break;
      case FeatureService.FEATURE_CPS_STD_AR_RT_MEAN:
      case FeatureService.FEATURE_DRAW_RIGHT_PWD_MEAN:
      case FeatureService.FEATURE_DRAW_LEFT_PWD_MEAN:
        clonedFeature.featureValue = clonedFeature.featureValue * -1;
        break;
      case FeatureService.FEATURE_DRAW_LEFT_DS_MEAN:
      case FeatureService.FEATURE_DRAW_RIGHT_DS_MEAN:
        clonedFeature.featureValue = clonedFeature.featureValue / 10;
        break;
      default:
        break;
    }

    return clonedFeature;
  }
}
